import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { captureException } from "@sentry/react";

import CustomToast from "shared-components/customToast";

import { deleteNexaThread, fetchNexaThreads, restoreNexaThread } from "../api";
import { NEXA_UNTITLED_THREAD } from "../constants";
import type { NexaThreadSummary } from "../types";

interface Props {
  activeThreadId: string | null;
  onSelect: (threadId: string) => void;
  onNewChat: () => void;
  refreshKey?: number;
  onDeleted?: (threadId: string) => void;
}

const UNDO_WINDOW_MS = 8000;

const TOAST_OPTIONS = {
  draggable: false,
  closeButton: false,
  className: "!tw-bg-gray-900 !tw-overflow-visible !tw-w-[390px] !tw-rounded-2xl",
};

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

const bucketFor = (iso: string): string => {
  const today = startOfDay(new Date());
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "Earlier";
  const days = Math.floor((today - startOfDay(new Date(then))) / 86400000);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 7) return "Earlier this week";
  return "Older";
};

const BUCKET_ORDER = ["Today", "Yesterday", "Earlier this week", "Older", "Earlier"];

const ThreadList = ({ activeThreadId, onSelect, onNewChat, refreshKey = 0, onDeleted }: Props) => {
  const [threads, setThreads] = useState<NexaThreadSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [localRefresh, setLocalRefresh] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    fetchNexaThreads()
      .then((data) => {
        if (!cancelled) setThreads(data);
      })
      .catch((err) => {
        captureException(err);
        if (!cancelled) setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [refreshKey, localRefresh]);

  const undo = useCallback(async (threadId: string, toastId: string | number) => {
    toast.dismiss(toastId);
    try {
      await restoreNexaThread(threadId);
    } catch (err) {
      captureException(err);
      toast(<CustomToast title="Couldn't restore that conversation" customId="error" />, { ...TOAST_OPTIONS, autoClose: 5000 });
    } finally {
      setLocalRefresh((k) => k + 1);
    }
  }, []);

  const remove = useCallback(
    async (thread: NexaThreadSummary) => {
      try {
        await deleteNexaThread(thread.threadId);
      } catch (err) {
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (status !== 404) {
          captureException(err);
          toast(<CustomToast title="Couldn't delete that conversation" customId="error" />, { ...TOAST_OPTIONS, autoClose: 5000 });
          return;
        }
      }

      setThreads((prev) => prev.filter((t) => t.threadId !== thread.threadId));
      onDeleted?.(thread.threadId);

      const toastId = toast(
        <CustomToast title="Conversation deleted" customId="success" showButton buttonText="Undo" onButtonClick={() => void undo(thread.threadId, toastId)} />,
        { ...TOAST_OPTIONS, autoClose: UNDO_WINDOW_MS }
      );
    },
    [onDeleted, undo]
  );

  const buckets = useMemo(() => {
    const map = new Map<string, NexaThreadSummary[]>();
    threads.forEach((thread) => {
      const bucket = bucketFor(thread.updatedAt);
      map.set(bucket, [...(map.get(bucket) ?? []), thread]);
    });
    return BUCKET_ORDER.filter((b) => map.has(b)).map((b) => [b, map.get(b)!] as const);
  }, [threads]);

  return (
    <div className="tw-flex tw-h-full tw-min-h-0 tw-flex-col">
      <div className="tw-shrink-0 tw-p-3">
        <button
          type="button"
          onClick={onNewChat}
          className="tw-flex tw-w-full tw-items-center tw-justify-center tw-gap-2 tw-rounded-lg tw-bg-[#1E1B4B] tw-px-3 tw-py-2.5 tw-text-label-md tw-font-medium tw-text-white hover:tw-bg-[#312E81]">
          <svg
            className="tw-shrink-0"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            height="20px"
            viewBox="0 -960 960 960"
            width="20px"
            fill="#ffffff">
            <path d="M444-444H240v-72h204v-204h72v204h204v72H516v204h-72v-204Z" />
          </svg>
          New chat
        </button>
      </div>

      <div className="tw-min-h-0 tw-flex-1 tw-overflow-y-auto tw-px-2 tw-pb-3">
        {loading && <div className="tw-px-2 tw-py-3 tw-text-label-xs tw-text-gray-500">Loading conversations…</div>}

        {failed && <div className="tw-px-2 tw-py-3 tw-text-label-xs tw-text-gray-500">Couldn't load your conversations.</div>}

        {!loading && !failed && threads.length === 0 && (
          <div className="tw-px-2 tw-py-3 tw-text-label-xs tw-leading-relaxed tw-text-gray-500">No conversations yet. Ask your first question to start one.</div>
        )}

        {buckets.map(([bucket, items]) => (
          <div key={bucket}>
            <div className="tw-px-2 tw-pb-1 tw-pt-3 tw-text-label-xs tw-font-semibold tw-uppercase tw-tracking-wider tw-text-gray-900">{bucket}</div>
            {items.map((thread) => {
              const active = thread.threadId === activeThreadId;
              return (
                <div key={thread.threadId} className={`tw-group tw-mb-0.5 tw-flex tw-items-center tw-gap-1 tw-rounded-lg hover:tw-bg-[#F1F1FA] ${active ? "tw-bg-[#EEF0FE]" : ""}`}>
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => onSelect(thread.threadId)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onSelect(thread.threadId);
                      }
                    }}
                    className={`tw-min-w-0 tw-flex-1 tw-cursor-pointer tw-truncate tw-rounded-lg tw-px-2 tw-py-2 tw-text-left tw-text-label-sm focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-[#4F46E5] ${
                      active ? "tw-font-medium tw-text-gray-900" : "tw-text-gray-500"
                    }`}>
                    {thread.title || NEXA_UNTITLED_THREAD}
                  </div>

                  <button
                    type="button"
                    aria-label={`Delete conversation: ${thread.title || NEXA_UNTITLED_THREAD}`}
                    onClick={() => void remove(thread)}
                    className="tw-mr-1 tw-shrink-0 tw-rounded-md tw-p-1.5 tw-text-gray-400 tw-opacity-0 hover:tw-bg-white hover:tw-text-error-600 focus:tw-opacity-100 group-hover:tw-opacity-100">
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                      <path
                        d="M2.8 4.4h10.4M6.4 4.4V3.2a1 1 0 0 1 1-1h1.2a1 1 0 0 1 1 1v1.2M4 4.4l.6 8a1 1 0 0 0 1 .9h4.8a1 1 0 0 0 1-.9l.6-8"
                        stroke="currentColor"
                        strokeWidth="1.3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ThreadList;
